import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type {
  DietPlan,
  DraftDay,
  DraftEntryDto,
  DraftResponse,
  PlannerMealSlot,
} from '../../api/plannerTypes.js';
import { useToast } from '../../hooks/useToast.js';
import { DayStrip } from './DayStrip.js';
import { MealSlotCard } from './MealSlotCard.js';
import { RecipeSwapModal } from './RecipeSwapModal.js';

export interface PlanDraftReviewProps {
  draft: DraftResponse;
  onSwap: (id: number, recipeId: number) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onConfirm: () => Promise<{ confirmed_count: number }>;
}

const SLOTS: readonly PlannerMealSlot[] = ['breakfast', 'lunch', 'dinner'];

function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

export function PlanDraftReview({
  draft,
  onSwap,
  onDelete,
  onConfirm,
}: PlanDraftReviewProps): ReactElement {
  const plan = draft.plan as DietPlan;
  const [selectedDate, setSelectedDate] = useState<string>(draft.days[0]?.date ?? '');
  const [swapSlot, setSwapSlot] = useState<PlannerMealSlot | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const toast = useToast();

  useEffect(() => {
    if (draft.days.length > 0 && !draft.days.some((d) => d.date === selectedDate)) {
      setSelectedDate(draft.days[0]!.date);
    }
  }, [draft.days, selectedDate]);

  const currentDay: DraftDay | undefined = useMemo(
    () => draft.days.find((d) => d.date === selectedDate),
    [draft.days, selectedDate],
  );

  function slotEntries(slot: PlannerMealSlot): DraftEntryDto[] {
    if (!currentDay) return [];
    return currentDay.entries.filter((e) => e.meal_type === slot);
  }

  async function handleDelete(entry: DraftEntryDto): Promise<void> {
    setBusy(true);
    try {
      await onDelete(entry.id);
    } catch (e) {
      toast.push('error', e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  }

  function openSwap(slot: PlannerMealSlot): void {
    setSwapSlot(slot);
  }

  async function handleConfirm(): Promise<void> {
    setBusy(true);
    try {
      const res = await onConfirm();
      toast.push('success', `Confirmed ${res.confirmed_count} meals`);
    } catch (e) {
      toast.push('error', e instanceof Error ? e.message : 'Confirm failed');
    } finally {
      setBusy(false);
    }
  }

  const ratio = currentDay ? currentDay.totals.kcal / Math.max(1, currentDay.target.kcal) : 0;
  const accurate = ratio >= 0.95 && ratio <= 1.05;

  return (
    <section
      className="flex flex-col"
      data-testid="planner-review"
    >
      <header
        className="flex flex-col gap-1 px-4 pt-4"
        data-testid="planner-review-header"
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-main)' }}>
          Plan Draft ({draft.start_date} – {draft.end_date})
        </h2>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {plan.name} · target {Math.round(plan.target_kcal_day)} kcal · {plan.target_protein_day}g protein
        </p>
      </header>

      <DayStrip days={draft.days} selected={selectedDate} onSelect={setSelectedDate} />

      {currentDay ? (
        <div className="flex flex-col gap-3 px-4">
          <div
            className="flex items-center gap-2 rounded-md border p-2 text-sm"
            style={{ borderColor: 'var(--border)', color: 'var(--text-main)' }}
            data-testid="planner-day-status"
          >
            <span>
              {currentDay.totals.kcal} / {currentDay.target.kcal} kcal ({fmtPct(ratio)})
            </span>
            <span aria-label={accurate ? 'on-target' : 'off-target'}>
              {accurate ? '🎯' : '⚠️'}
            </span>
          </div>
          {SLOTS.map((slot) => {
            const entries = slotEntries(slot);
            if (entries.length === 0) {
              return (
                <div
                  key={slot}
                  className="rounded-xl border p-3 text-sm"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                  data-testid={`planner-meal-${slot}`}
                >
                  No {slot} planned.
                </div>
              );
            }
            return entries.map((entry) => (
              <MealSlotCard
                key={entry.id}
                slot={slot}
                entry={entry}
                onSwap={(e) => openSwap(e.meal_type)}
                onDelete={handleDelete}
              />
            ));
          })}
        </div>
      ) : (
        <div className="px-4 text-sm" style={{ color: 'var(--text-muted)' }}>
          No day selected.
        </div>
      )}

      <div className="p-4">
        <button
          type="button"
          data-testid="planner-confirm"
          onClick={() => void handleConfirm()}
          disabled={busy}
          className="flex w-full min-h-[48px] items-center justify-center rounded-md px-4 text-base font-semibold disabled:opacity-50"
          style={{
            background: 'var(--accent)',
            color: 'var(--bg-surface)',
          }}
        >
          Confirm &amp; Apply Plan
        </button>
      </div>

      {swapSlot !== null ? (
        <RecipeSwapModal
          open={swapSlot !== null}
          onClose={() => setSwapSlot(null)}
          slot={swapSlot}
          plan={plan}
          dayTarget={currentDay?.target ?? { kcal: plan.target_kcal_day, protein_g: plan.target_protein_day }}
          onPick={async (recipeId) => {
            const entry = slotEntries(swapSlot)[0];
            if (!entry) return;
            try {
              await onSwap(entry.id, recipeId);
            } catch (e) {
              toast.push('error', e instanceof Error ? e.message : 'Swap failed');
            }
          }}
        />
      ) : null}
    </section>
  );
}